import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import {
  Alert,
  Button,
  Disclosure,
  Modal,
  Spinner,
  StatusPill,
} from "@playbookai/ui";
import type {
  AdminConnection,
  ConnectionTestResult,
} from "../../../api/admin-ai-profiles-api.js";
import { useDateTimeFormatter } from "../../../settings/format-date-time.js";
import { AdminTimeZoneLabel } from "../AdminTimeZoneLabel.js";

export interface ConnectionTestRun {
  tenant: string | null | undefined;
  connection: AdminConnection;
  startedAt: number;
  finishedAt?: number;
  result?: ConnectionTestResult;
  requestFailed?: boolean;
  errorCode?: string | undefined;
}
const checks: Record<string, string> = {
  configuration: "Configuration",
  generation_streaming: "Generation and streaming",
  structured_output: "Structured output",
  tool_round_trip: "Tool calling round trip",
  native_continuation: "Conversation continuation",
  reasoning: "Reasoning",
  hosted_search: "Hosted search",
  embedding: "Embeddings",
};
const outcomes: Record<string, string> = {
  passed: "Passed",
  failed: "Failed",
  not_tested: "Not tested",
  unsupported: "Unsupported",
};
const errors: Record<string, string> = {
  PROVIDER_AUTH_FAILED:
    "Authentication failed. Check the credential reference, identity configuration and provider permissions.",
  PROVIDER_THROTTLED:
    "The provider rate limit was reached. Check available quota and retry later.",
  PROVIDER_TIMEOUT:
    "The provider did not respond in time. Check connectivity and retry.",
  PROVIDER_UNAVAILABLE:
    "The provider could not be reached. Check its availability, endpoint and network access.",
  PROVIDER_REQUEST_REJECTED:
    "The provider rejected the request. Check the model or deployment name, API version and token ceilings.",
  PROVIDER_CONTENT_FILTERED:
    "The provider blocked the test request. Ask your operator to review the provider content policy.",
  MODEL_CONNECTION_INCOMPATIBLE:
    "The connection does not support the requested test. Check its model, protocol, API version and capabilities.",
  OUTPUT_VALIDATION_FAILED:
    "A model response did not pass validation. Review the failed check, model capabilities and output token ceiling.",
  INVALID_REQUEST:
    "The connection configuration could not be tested. Review its fields and try again.",
  SECRET_NOT_FOUND:
    "The credential reference could not be resolved. Ask your operator to check the secret and access permissions.",
};
export function connectionTestError(code?: string | null): string {
  return (
    (code && errors[code]) ||
    "The connection test could not be completed successfully. Review the connection settings and ask your operator to investigate if it continues."
  );
}
export function ConnectionTestDialog({
  open,
  run,
  providerLabel,
  enabled,
  enabling,
  enableError,
  onEnable,
  onRetry,
  onClose,
}: {
  /** Hoisted from the mount so the dialog can animate out (ADR-0091). */
  open: boolean;
  run: ConnectionTestRun;
  providerLabel: string;
  enabled: boolean;
  enabling: boolean;
  enableError: string | null;
  onEnable: () => void;
  onRetry: () => void;
  onClose: () => void;
}) {
  const formatter = useDateTimeFormatter();
  const { dateTime } = formatter;
  const running = run.finishedAt === undefined;
  const [elapsed, setElapsed] = useState(() =>
    Math.max(
      0,
      Math.floor(((run.finishedAt ?? Date.now()) - run.startedAt) / 1000),
    ),
  );
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  useEffect(() => {
    // Also gated on `open`: the dialog is retained while closed (ADR-0091), and
    // a closed dialog counting seconds nobody can see is a timer for nothing.
    if (!running || !open) return;
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - run.startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [open, run.startedAt, running]);
  const seconds =
    run.finishedAt === undefined
      ? elapsed
      : Math.max(0, Math.floor((run.finishedAt - run.startedAt) / 1000));
  const result = run.result;
  const passed = result?.status === "passed";
  const code = result?.error_code ?? run.errorCode;
  // Only render known checks/statuses and controlled error copy. Provider payloads,
  // response previews and arbitrary error bodies never enter the diagnostic export.
  const reported = Object.entries(checks).flatMap(([key, label]) => {
    const outcome = result?.details?.checks?.[key];
    return outcome && Object.hasOwn(outcomes, outcome)
      ? [{ key, label, outcome }]
      : [];
  });
  const transportResults = ["websocket", "http"].flatMap((transport) => {
    const entry = result?.details?.transports?.[transport];
    if (!entry || !["passed", "failed"].includes(entry.status)) return [];
    return [
      {
        transport,
        status: entry.status,
        errorCode: entry.error_code,
        checks: Object.entries(checks).flatMap(([key, label]) => {
          const outcome = entry.details?.checks?.[key];
          return outcome && Object.hasOwn(outcomes, outcome)
            ? [{ key, label, outcome }]
            : [];
        }),
      },
    ];
  });
  const message = run.requestFailed
    ? "AIS did not return a test result. Check your session and service connectivity, then refresh the connection before retrying. The provider test may have continued."
    : connectionTestError(code);
  const diagnostics = JSON.stringify(
    {
      connection_id: run.connection.id,
      provider: providerLabel,
      model_or_deployment: run.connection.model_or_deployment,
      started_at: new Date(run.startedAt).toISOString(),
      duration_seconds: seconds,
      status: run.requestFailed ? "result_unavailable" : result?.status,
      error_code: code && Object.hasOwn(errors, code) ? code : null,
      message: passed ? "Connection test passed." : message,
      transports: Object.fromEntries(
        transportResults.map((entry) => [
          entry.transport,
          {
            status: entry.status,
            error_code:
              entry.errorCode && Object.hasOwn(errors, entry.errorCode)
                ? entry.errorCode
                : null,
            checks: Object.fromEntries(
              entry.checks.map(({ key, outcome }) => [key, outcome]),
            ),
          },
        ]),
      ),
      checks: Object.fromEntries(
        reported.map(({ key, outcome }) => [key, outcome]),
      ),
    },
    null,
    2,
  );
  async function copy() {
    try {
      await navigator.clipboard.writeText(diagnostics);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }
  return (
    <Modal open={open} title="Test connection" onClose={onClose} size="lg">
      <div className="grid min-w-0 gap-4">
        <div className="grid gap-1 break-words">
          <p className="font-medium text-pb-text">
            {run.connection.display_name}
          </p>
          <p className="text-sm text-pb-text-muted">
            {providerLabel} · {run.connection.model_or_deployment}
          </p>
        </div>
        {running ? (
          <>
            {/* Look row OK-38: the LoaderCircle glyph becomes the Spinner ring. */}
            <div className="flex items-center text-sm text-pb-text">
              <Spinner size="md" label="Testing connection…" />
            </div>
            <p className="text-xs text-pb-text-muted">Elapsed: {seconds}s</p>
            <p className="text-sm text-pb-text-muted">
              You can close this dialog. The test will continue; reopen it from
              the connection row to see progress or results.
            </p>
          </>
        ) : (
          <>
            <Alert tone={passed ? "healthy" : "critical"}>
              <p className="font-medium">
                {passed
                  ? "Connection test passed"
                  : run.requestFailed
                    ? "Test result unavailable"
                    : "Connection test failed"}
              </p>
              {!passed && <p className="mt-1">{message}</p>}
            </Alert>
            <p className="text-xs text-pb-text-muted">
              {/* The duration is a length and needs no zone; the start is an
                  instant and does. */}
              Duration: {seconds}s · Started{" "}
              <AdminTimeZoneLabel
                label={dateTime(run.startedAt)}
                formatter={formatter}
                variant="inline"
              />
            </p>
            {transportResults.length > 0 ? (
              <div className="grid gap-4">
                {transportResults.map((entry) => (
                  <section
                    key={entry.transport}
                    aria-label={`${entry.transport === "http" ? "HTTP" : "WebSocket"} qualification`}
                    className="grid gap-2 rounded-md border border-pb-border p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="text-sm font-medium text-pb-text">
                        {entry.transport === "http" ? "HTTP" : "WebSocket"}
                      </h3>
                      <StatusPill
                        tone={
                          entry.status === "passed" ? "healthy" : "critical"
                        }
                      >
                        {outcomes[entry.status]}
                      </StatusPill>
                    </div>
                    {entry.status === "failed" && (
                      <p className="text-xs text-pb-text-muted">
                        {connectionTestError(entry.errorCode)}
                      </p>
                    )}
                    <ul className="grid gap-1 text-xs text-pb-text-muted">
                      {entry.checks.map(({ key, label, outcome }) => (
                        <li key={key} className="flex justify-between gap-2">
                          <span>{label}</span>
                          <span>{outcomes[outcome]}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                <p className="text-xs text-pb-text-muted">
                  All selected transports must pass before enabling this
                  connection.
                </p>
              </div>
            ) : reported.length > 0 ? (
              <ul aria-label="Test checks" className="grid gap-2">
                {reported.map(({ key, label, outcome }) => (
                  <li
                    key={key}
                    className="flex items-center justify-between gap-3 text-sm text-pb-text"
                  >
                    <span>{label}</span>
                    <StatusPill
                      tone={
                        outcome === "passed"
                          ? "healthy"
                          : outcome === "failed"
                            ? "critical"
                            : "neutral"
                      }
                    >
                      {outcomes[outcome]}
                    </StatusPill>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-pb-text-muted">
                Individual check results were not returned.
              </p>
            )}
            <p className="text-xs text-pb-text-muted">
              Only reported checks are qualified. Missing or untested
              capabilities are not confirmed.
            </p>
            {/* A bare <details> can never animate its panel (ADR-0091); the
                design system's Disclosure opens on a 0fr → 1fr grid row and
                keeps the collapsed body inert. */}
            <Disclosure
              className="rounded-md border border-pb-border p-3"
              caret={<ChevronRight className="h-4 w-4" aria-hidden />}
              summary={
                <span className="text-sm font-medium text-pb-text">
                  {passed ? "Diagnostic details" : "Error details"}
                </span>
              }
            >
              <pre className="mt-3 whitespace-pre-wrap break-all text-xs text-pb-text-muted">
                {diagnostics}
              </pre>
              <p className="my-3 text-xs text-pb-text-muted">
                No request ID was returned by this endpoint. Your operator can
                use the connection ID and timestamp to investigate.
              </p>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  void copy();
                }}
              >
                Copy diagnostic details
              </Button>
              {copyState !== "idle" && (
                <p role="status" className="mt-2 text-xs text-pb-text-muted">
                  {copyState === "copied"
                    ? "Copied."
                    : "Could not access the clipboard. Select and copy the details above."}
                </p>
              )}
            </Disclosure>
            {enableError && (
              <Alert tone="critical" title="Couldn't enable the connection">
                {enableError}
              </Alert>
            )}
          </>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          {!running && (
            <Button
              type="button"
              variant="secondary"
              disabled={enabling}
              onClick={onRetry}
            >
              Test again
            </Button>
          )}
          {passed && !enabled && (
            <Button
              type="button"
              variant="primary"
              disabled={enabling}
              onClick={onEnable}
            >
              {enabling ? "Enabling…" : "Enable connection"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
